// Design System Constants - Chevron Corporate Branding
// Centralized styling values for consistent UI across the application
// Using official Chevron brand colors and corporate styling

export const DESIGN_SYSTEM = {
  // Typography - Corporate style
  typography: {
    // Font sizes
    fontSize: {
      xs: '0.75rem',    // 12px - labels, captions
      sm: '0.875rem',   // 14px - body text, inputs
      md: '1rem',       // 16px - headings
      lg: '1.125rem',   // 18px - large headings
      xl: '1.5rem',     // 24px - section titles
      xxl: '2rem',      // 32px - page titles
    },
    // Font weights
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    // Line heights
    lineHeight: {
      tight: 1.2,
      normal: 1.4,
      relaxed: 1.6,
    },
  },

  // Spacing (8px base unit)
  spacing: {
    xs: 4,    // 4px
    sm: 8,    // 8px
    md: 16,   // 16px
    lg: 24,   // 24px
    xl: 32,   // 32px
    xxl: 48,  // 48px
  },

  // Chevron Brand Colors
  colors: {
    // Chevron Primary Blue
    primary: {
      main: '#0054A4',      // Chevron Blue
      light: '#3B7CC4',     // Lighter blue for hover states
      dark: '#003A73',      // Darker blue for pressed states
      50: '#E6F0F9',        // Very light blue for backgrounds
      100: '#CCE0F3',
      200: '#99C1E6',
      300: '#66A3DA',
      400: '#337FCE',
      500: '#0054A4',
      600: '#004A93',
      700: '#003A73',
      800: '#002B55',
      900: '#001C37',
    },
    // Chevron Red (accent)
    accent: {
      main: '#E4002B',      // Chevron Red
      light: '#FF3358',     // Lighter red
      dark: '#B3001F',      // Darker red
    },
    // Secondary colors (complementary)
    secondary: {
      main: '#1E3A5F',      // Deep navy for secondary elements
      light: '#2E5A8F',
      dark: '#0F1E2F',
    },
    // Status colors
    status: {
      success: '#28A745',   // Green for success
      warning: '#F7941D',   // Orange for warnings
      error: '#E4002B',     // Chevron Red for errors
      info: '#0054A4',      // Chevron Blue for info
    },
    // Neutral colors - Professional grays
    neutral: {
      50: '#F8F9FA',
      100: '#F1F3F5',
      200: '#E9ECEF',
      300: '#DEE2E6',
      400: '#ADB5BD',
      500: '#6C757D',
      600: '#495057',
      700: '#343A40',
      800: '#212529',
      900: '#0D0D0D',
    },
    // Text colors
    text: {
      primary: '#1A1A1A',
      secondary: '#5A6268',
      disabled: '#ADB5BD',
      inverse: '#FFFFFF',
    },
    // Background colors
    background: {
      primary: '#FFFFFF',
      secondary: '#F8F9FA',
      dark: '#0D1B2A',
      darker: '#0A1421',
    },
  },

  // Component dimensions
  dimensions: {
    // Form elements
    inputHeight: 32,
    inputHeightSmall: 28,
    inputHeightLarge: 40,
    
    // Buttons
    buttonHeight: 32,
    buttonHeightSmall: 24,
    buttonHeightLarge: 40,
    
    // Icons
    iconSize: {
      xs: 16,
      sm: 20,
      md: 24,
      lg: 32,
      xl: 48,
    },
    
    // Borders
    borderWidth: 1,
    borderRadius: 4,
    borderRadiusSmall: 2,
    borderRadiusLarge: 8,
  },

  // Shadows
  shadows: {
    sm: '0 1px 2px rgba(0,0,0,0.05)',
    md: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
    lg: '0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)',
    xl: '0 10px 15px rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.05)',
  },

  // Z-index scale - organized hierarchy from bottom to top
  zIndex: {
    // Background layer
    background: 1,
    connection: 1,
    
    // Content layer
    node: 10,
    nodeHovered: 25,
    nodeSelected: 50,
    
    // Interactive layer
    connectionHandle: 60,
    anchorIndicator: 70,
    
    // Selection layer
    selectionRect: 80,
    
    // UI overlay layer
    floatingToolbar: 200,
    minimap: 300,
    legend: 1200,  // Increased to be above ServiceNow panel and other elements
    
    // Notification layer
    toast: 500,
    
    // Legacy high-level UI (keeping for compatibility)
    dropdown: 1030,
    sticky: 1020,
    fixed: 1030,
    
    // Header-triggered elements (above header)
    headerDropdown: 1040,
    headerPanel: 1045,
    
    // Modal and overlay elements
    modalBackdrop: 1050,
    modal: 1150,    // Increased to be above popover
    popover: 1300,  // Increased to be above legend
    tooltip: 1080,
  },
} as const;

// Helper functions for consistent styling
export const getFormFieldStyles = () => ({
  label: {
    fontSize: DESIGN_SYSTEM.typography.fontSize.xs,
    fontWeight: DESIGN_SYSTEM.typography.fontWeight.medium,
    color: DESIGN_SYSTEM.colors.text.secondary,
    marginBottom: DESIGN_SYSTEM.spacing.xs,
    display: 'block',
  },
  input: {
    fontSize: DESIGN_SYSTEM.typography.fontSize.sm,
    lineHeight: DESIGN_SYSTEM.typography.lineHeight.normal,
    minHeight: DESIGN_SYSTEM.dimensions.inputHeight,
  },
  helperText: {
    fontSize: DESIGN_SYSTEM.typography.fontSize.xs,
    marginTop: DESIGN_SYSTEM.spacing.xs,
  },
});

export const getButtonStyles = (variant: 'primary' | 'secondary' | 'outline' = 'primary') => ({
  height: DESIGN_SYSTEM.dimensions.buttonHeight,
  fontSize: DESIGN_SYSTEM.typography.fontSize.sm,
  fontWeight: DESIGN_SYSTEM.typography.fontWeight.medium,
  borderRadius: DESIGN_SYSTEM.dimensions.borderRadius,
  textTransform: 'none' as const,
  ...(variant === 'primary' && {
    backgroundColor: DESIGN_SYSTEM.colors.primary.main,
    color: 'white',
    '&:hover': {
      backgroundColor: DESIGN_SYSTEM.colors.primary.dark,
    },
  }),
  ...(variant === 'secondary' && {
    backgroundColor: DESIGN_SYSTEM.colors.secondary.main,
    color: 'white',
    '&:hover': {
      backgroundColor: DESIGN_SYSTEM.colors.secondary.dark,
    },
  }),
  ...(variant === 'outline' && {
    backgroundColor: 'transparent',
    color: DESIGN_SYSTEM.colors.primary.main,
    border: `1px solid ${DESIGN_SYSTEM.colors.primary.main}`,
    '&:hover': {
      backgroundColor: DESIGN_SYSTEM.colors.primary.light + '20',
    },
  }),
});

export const getCardStyles = () => ({
  padding: DESIGN_SYSTEM.spacing.md,
  borderRadius: DESIGN_SYSTEM.dimensions.borderRadius,
  boxShadow: DESIGN_SYSTEM.shadows.md,
  backgroundColor: DESIGN_SYSTEM.colors.neutral[50],
  border: `1px solid ${DESIGN_SYSTEM.colors.neutral[200]}`,
});

export const getSectionHeaderStyles = () => ({
  fontSize: DESIGN_SYSTEM.typography.fontSize.sm,
  fontWeight: DESIGN_SYSTEM.typography.fontWeight.semibold,
  color: DESIGN_SYSTEM.colors.primary.main,
  marginBottom: DESIGN_SYSTEM.spacing.sm,
});

export const getChipStyles = (color: 'success' | 'warning' | 'error' | 'info' | 'default' = 'default') => ({
  height: 24,
  fontSize: DESIGN_SYSTEM.typography.fontSize.xs,
  fontWeight: DESIGN_SYSTEM.typography.fontWeight.medium,
  borderRadius: DESIGN_SYSTEM.dimensions.borderRadius,
  ...(color === 'success' && {
    backgroundColor: DESIGN_SYSTEM.colors.status.success,
    color: 'white',
  }),
  ...(color === 'warning' && {
    backgroundColor: DESIGN_SYSTEM.colors.status.warning,
    color: 'white',
  }),
  ...(color === 'error' && {
    backgroundColor: DESIGN_SYSTEM.colors.status.error,
    color: 'white',
  }),
  ...(color === 'info' && {
    backgroundColor: DESIGN_SYSTEM.colors.status.info,
    color: 'white',
  }),
  ...(color === 'default' && {
    backgroundColor: DESIGN_SYSTEM.colors.neutral[200],
    color: DESIGN_SYSTEM.colors.text.primary,
  }),
});

// Export the design system for easy access
export default DESIGN_SYSTEM;
